import React, { useEffect, useMemo, useState } from "react";
import PinCell from "@/components/Tiles/Sense/TableView/PinCell";
import { format } from "date-fns";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/aible/DropdownMenu/DropdownMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/aible/Table/Table";
import SortingHeaderCell from "@/components/Tiles/Sense/TableView/SortingHeaderCell";
import { useDispatch } from "@/types/store";
import CellWithTooltip from "@/components/Tiles/Sense/TableView/CellWithTooltip";
import { Sheet, SheetContent } from "@/aible/Sheet/Sheet";
import SenseTile from "@/components/Tiles/Sense/SenseTile";
import { cn } from "@/utils/cn";
import UnstructuredRowHoverActions from "@/components/Tiles/Sense/TableView/UnstructuredRowHoverActions";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { deleteCorpus } from "@/containers/LanguageModelArtifact/reducer";
const UnstructuredTable = ({ dataProp }) => {
    const dispatch = useDispatch();
    const columns = useMemo(() => [
        {
            id: "pin",
            header: "",
            accessorKey: "isPinned",
            cell: ({ row }) => React.createElement(PinCell, { id: row.original.id, updatePin: row.pin, type: "CORPUS" }),
            enableSorting: false
        },
        {
            id: "name",
            accessorKey: "name",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "Data Name" }),
            cell: ({ row }) => (React.createElement("div", { className: "flex items-center" },
                row.original?.status === "INPROGRESS" && (React.createElement("div", { style: { display: "flex", alignItems: "center", marginRight: "10px" } },
                    React.createElement(LoadingIndicator, { style: { marginLeft: "-9px", scale: "0.5", marginTop: "0", width: "32px", height: "6px" } }))),
                React.createElement(CellWithTooltip, { className: "max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap", tooltip: React.createElement("div", null, row.original.name) }, row.original.name))),
            enableMultiSort: false,
            sortingFn: "alphanumeric"
        },
        {
            id: "id",
            accessorKey: "id",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "ID" }),
            enableMultiSort: false,
            sortingFn: "basic"
        },
        {
            id: "documentCount",
            accessorKey: "documentCount",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "Documents" }),
            enableMultiSort: false,
            sortingFn: "basic"
        },
        {
            id: "source",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "Source" }),
            accessorKey: "source",
            enableMultiSort: false,
            sortingFn: "text"
        },
        {
            id: "createdDate",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "Date Created" }),
            accessorKey: "createdDate",
            cell: ({ row }) => React.createElement("div", null, format(new Date(row.original.createdDate), "LLL dd, yyyy")),
            enableMultiSort: false,
            sortingFn: "datetime"
        },
        {
            id: "owner",
            header: ({ column }) => React.createElement(SortingHeaderCell, { column: column, text: "Owner" }),
            accessorKey: "owner",
            enableMultiSort: false,
            sortingFn: "text",
            cell: ({ row }) => (React.createElement(CellWithTooltip, { className: "max-w-[210px] overflow-hidden text-ellipsis whitespace-nowrap", tooltip: React.createElement("div", null, row.original.owner) },
                React.createElement("div", null, row.original.owner)))
        },
        {
            id: "actions",
            header: "",
            accessorKey: "actions",
            cell: ({ row }) => {
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement("div", { className: "cursor-pointer w-5" },
                        React.createElement(DropdownMenu, null,
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(FontAwesomeIcon, { icon: faEllipsisVertical })),
                            React.createElement(DropdownMenuContent, { className: "w-56", align: "end" },
                                React.createElement(DropdownMenuItem, { variant: "destructive", onClick: (e) => {
                                        e.stopPropagation();
                                        dispatch(deleteCorpus(Number(row.original.id)));
                                    } }, "Delete"))))));
            },
            enableSorting: false
        }
    ], []);
    const [data, setData] = useState(dataProp);
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState([]);
    const [rowPinning, setRowPinning] = useState({
        top: []
    });
    const table = useReactTable({
        columns,
        data: data || [],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: { rowPinning, sorting, rowSelection },
        onSortingChange: setSorting,
        onRowPinningChange: setRowPinning,
        getRowId: (row) => row.id.toString(),
        onRowSelectionChange: setRowSelection,
        initialState: {
            sorting: [
                {
                    id: "createdDate",
                    desc: true
                }
            ]
        },
        keepPinnedRows: true
    });
    const haveData = Boolean(table.getRowModel().rows?.length + table.getTopRows()?.length);
    const selectedRowId = Object.keys(rowSelection)[0];
    useEffect(() => {
        setRowPinning({
            top: dataProp.filter((item) => item.isPinned).map((item) => item.id.toString())
        });
        setData(dataProp);
    }, [dataProp]);
    return (React.createElement("div", { className: `mx-2 p-5 bg-white rounded-sm border border-black/20` },
        React.createElement("div", { className: "w-full" },
            React.createElement("div", { className: "overflow-hidden rounded-md border" },
                React.createElement(Table, null,
                    React.createElement(TableHeader, null, table.getHeaderGroups().map((headerGroup) => (React.createElement(TableRow, { key: headerGroup.id }, headerGroup.headers.map((header) => {
                        return (React.createElement(TableHead, { key: header.id, className: "font-bold" }, header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())));
                    }))))),
                    React.createElement(TableBody, null,
                        !haveData && (React.createElement(TableRow, null,
                            React.createElement(TableCell, { colSpan: columns?.length, className: "h-24 text-center" }, "No results."))),
                        table.getTopRows().map((row) => (React.createElement(TableRow, { key: row.id, className: cn("group relative cursor-pointer hover:outline-1 hover:outline-(--purple) hover:outline-offset-[-1px]", Number(selectedRowId) === row.original.id &&
                                "bg-(--highlight-purple) outline-1 outline-(--purple) outline-offset-[-1px]") },
                            row.getVisibleCells().map((cell) => (React.createElement(TableCell, { key: cell.id, onClick: () => {
                                    if (selectedRowId && selectedRowId !== row.id) {
                                        table.resetRowSelection();
                                    }
                                    row.toggleSelected(selectedRowId !== row.id);
                                } }, flexRender(cell.column.columnDef.cell, cell.getContext())))),
                            React.createElement("div", { className: cn("cursor-pointer opacity-0 invisible bg-linear-to-r from-transparent via-[20px] via-(--white) to-(--white) to-100% absolute space-x-2.5 right-[35px] w-0 h-full flex items-center justify-end group-hover:opacity-100 group-hover:visible group-hover:w-[510px]  group-hover:h-[calc(100%-2px)]   group-hover:top-[1px]", Number(selectedRowId) === row.original.id && "bg-none"), onClick: () => {
                                    if (selectedRowId && selectedRowId !== row.id) {
                                        table.resetRowSelection();
                                    }
                                    row.toggleSelected(selectedRowId !== row.id);
                                } },
                                React.createElement(UnstructuredRowHoverActions, { artefactId: row.original.id }))))),
                        table.getCenterRows().map((row) => (React.createElement(TableRow, { key: row.id, className: cn("group relative cursor-pointer hover:outline-1 hover:outline-(--purple) hover:outline-offset-[-1px]", Number(selectedRowId) === row.original.id &&
                                "bg-(--highlight-purple) outline-1 outline-(--purple) outline-offset-[-1px]") },
                            row.getVisibleCells().map((cell) => (React.createElement(TableCell, { key: cell.id, onClick: () => {
                                    if (selectedRowId && selectedRowId !== row.id) {
                                        table.resetRowSelection();
                                    }
                                    row.toggleSelected(selectedRowId !== row.id);
                                } }, flexRender(cell.column.columnDef.cell, cell.getContext())))),
                            React.createElement("div", { className: cn("cursor-pointer opacity-0 invisible bg-linear-to-r from-transparent via-[20px] via-(--white) to-(--white) to-100% absolute space-x-2.5 right-[35px] w-0 h-full flex items-center justify-end group-hover:opacity-100 group-hover:visible group-hover:w-[510px]  group-hover:h-[calc(100%-2px)] group-hover:top-[1px]", Number(selectedRowId) === row.original.id && "bg-none"), onClick: () => {
                                    if (selectedRowId && selectedRowId !== row.id) {
                                        table.resetRowSelection();
                                    }
                                    row.toggleSelected(selectedRowId !== row.id);
                                } },
                                React.createElement(UnstructuredRowHoverActions, { artefactId: row.original.id }))))))))),
        React.createElement(Sheet, { key: "right", open: table.getIsSomeRowsSelected(), modal: false },
            React.createElement(SheetContent, { side: "right", className: "h-[calc(100vh-120px)] mt-[120px] data-[side=bottom]:max-h-[50vh] data-[side=top]:max-h-[50vh] sm:max-w-[523px] overflow-y-auto", noOverlay: true, showCloseButton: false, onEscapeKeyDown: () => table.resetRowSelection() }, selectedRowId && React.createElement(SenseTile, { id: Number(selectedRowId), type: "CORPUS", style: { marginBottom: 0 } })))));
};
export default UnstructuredTable;
